import React, { useState, useRef, useEffect, useContext } from 'react';
import {
  ModalHeader,
  ModalBody,
  ModalFooter,
  LoadingButton,
  Typography,
  FormStatusMessage,
  FormLabel,
  Input,
  FormController,
  Button,
} from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import ReCAPTCHA from 'react-google-recaptcha';
import { IPaymentCardCommonData } from '../../../models/common/payments';
import { IModalBasicProps } from '../../../models/common/modal';
import { GiftCardLogoAndNumber } from '../gift-card/logo-number';
import { validationSchema } from './validations';
import { getTextFieldStatus } from '../../../utils/form';
import { InfoPopover } from '../../shared/popover/popover';
import { GIFT_CARD_BACKSIDE_IMG_URL } from '../../../constants/popover-content';
import { getSiteKey } from '../../../utils/general';
import { TCheckGiftCardBalanceFormValues } from '../../../models/b2b/payments';

export interface ICheckGiftCardBalanceModalProps extends IModalBasicProps {
  card: IPaymentCardCommonData;
  isLoading: boolean;
  wrongPin: boolean;
  requestGiftCardBalance: (pin: string, recaptcha: string) => void;
}

export const CheckGiftCardBalanceModal = ({
  card,
  isLoading,
  wrongPin,
  hideModal,
  requestGiftCardBalance,
}: ICheckGiftCardBalanceModalProps) => {
  const { channel } = useContext(ExperienceContext);
  const [isPinIncorrect, setIsPinIncorrect] = useState(false);
  const isMobile = channel === 'mobile';
  const iconSize = isMobile ? 'small' : 'regular';
  const ReCAPTCHARef = useRef<ReCAPTCHA>(null);
  const defaultValues = {
    pin: '',
    recaptcha: '',
  };

  const formMethods = useForm<TCheckGiftCardBalanceFormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  const {
    control,
    formState: { errors: formErrors },
    handleSubmit,
    resetField,
  } = formMethods;

  const resetReCaptcha = () => {
    resetField('recaptcha');
    ReCAPTCHARef.current?.reset();
  };

  useEffect(() => {
    if (wrongPin) {
      setIsPinIncorrect(true);
    }
  }, [wrongPin]);

  const onSubmitForm: SubmitHandler<TCheckGiftCardBalanceFormValues> = async ({
    pin,
    recaptcha,
  }) => {
    requestGiftCardBalance(pin, recaptcha);
    resetReCaptcha();
  };

  const popoverContent = {
    title: '',
    message: (
      <>
        <img
          src={GIFT_CARD_BACKSIDE_IMG_URL}
          width="375"
          height="187"
          alt="Back of Gift Card"
        />
        <Typography variant="body-xs">
          This is your 4-digit unique identifier
        </Typography>
      </>
    ),
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <ModalHeader onClose={hideModal}>Check Gift Card Balance</ModalHeader>
        <ModalBody>
          <div className="sui-flex sui-flex-col sui-gap-y-6 sui-mb-5">
            <div className="sui-flex sui-flex-wrap">
              <GiftCardLogoAndNumber card={card} shouldShowInactive />
            </div>

            <Controller
              name="pin"
              control={control}
              render={({ field: { ref, onChange, ...rest } }) => {
                return (
                  <FormController>
                    <FormLabel htmlFor="pin">
                      Card PIN*
                      {popoverContent && (
                        <InfoPopover
                          iconSize={iconSize}
                          title={popoverContent.title}
                          placement={isMobile ? 'bottom' : 'right'}
                        >
                          <Typography variant="body-base">
                            {popoverContent.message}
                          </Typography>
                        </InfoPopover>
                      )}
                    </FormLabel>
                    <Input
                      {...rest}
                      id="pin"
                      fullWidth
                      type="text"
                      status={getTextFieldStatus(formErrors.pin)}
                      onChange={(event: KeyboardEvent) => {
                        if (isPinIncorrect) {
                          setIsPinIncorrect(false);
                        }
                        onChange(event);
                      }}
                      inputProps={{
                        'data-testid': 'pin',
                        maxLength: 4,
                      }}
                    />
                    <FormStatusMessage status="error">
                      {formErrors.pin?.message}
                    </FormStatusMessage>
                    {isPinIncorrect && (
                      <FormStatusMessage status="error">
                        We don&apos;t recognize this PIN. Review the card
                        details and try again.
                      </FormStatusMessage>
                    )}
                  </FormController>
                );
              }}
            />
            <div>
              <Controller
                name="recaptcha"
                control={control}
                render={({ field: { onChange } }) => (
                  <ReCAPTCHA
                    ref={ReCAPTCHARef}
                    sitekey={getSiteKey(true)}
                    onChange={onChange}
                    size={isMobile ? 'compact' : 'normal'}
                    data-testid="recaptcha"
                  />
                )}
              />
              <FormStatusMessage status="error">
                {formErrors.recaptcha?.message}
              </FormStatusMessage>
            </div>
          </div>
        </ModalBody>
        <ModalFooter position={isMobile ? 'center' : 'end'}>
          <div className="sui-flex sui-p-2 sui-gap-4 sui-flex-col-reverse sui-w-full sm:sui-flex-row sm:sui-w-auto">
            <Button variant="secondary" onClick={hideModal}>
              Cancel
            </Button>
            <LoadingButton
              loading={isLoading}
              variant="primary"
              type="submit"
              data-testid="submitBtn"
            >
              Check Card Balance
            </LoadingButton>
          </div>
        </ModalFooter>
      </form>
    </FormProvider>
  );
};
